import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FiBold, FiDroplet, FiItalic, FiUnderline, FiX } from 'react-icons/fi';
import { htmlToMarkup, markupToHtml } from '../utils/richText';
import './RichTextField.css';

/**
 * Single-line rich-text field for admin-authored text (home layout section
 * titles). It shows the text FORMATTED — the `[b]…[/b]` markup that is stored
 * never appears on screen — and a toolbar bubble surfaces over a selection,
 * the way a document editor does, rather than sitting there permanently.
 *
 * The value in and out is markup; `markupToHtml` / `htmlToMarkup` bridge it to
 * the contentEditable, and formatting is applied with `document.execCommand`.
 * execCommand is deprecated but has no replacement with this reach, and every
 * shape it can leave behind is normalized on the way back out — so the messy
 * HTML browsers disagree about never reaches the draft.
 */

export interface RichTextFieldProps {
  /** Markup, e.g. `Make it [b]pop[/b]`. */
  value: string;
  onChange: (markup: string) => void;
  placeholder?: string;
  /** Swatches offered in the bubble; the last button is always a custom picker. */
  colors: string[];
  ariaLabel?: string;
}

/** Which marks the selection currently carries, for the pressed-button state. */
interface ActiveMarks {
  bold: boolean;
  italic: boolean;
  underline: boolean;
}

/** Room the bubble needs above the selection before it flips underneath it. */
const BUBBLE_CLEARANCE = 60;

const readActiveMarks = (): ActiveMarks => ({
  bold: document.queryCommandState('bold'),
  italic: document.queryCommandState('italic'),
  underline: document.queryCommandState('underline'),
});

export default function RichTextField({ value, onChange, placeholder, colors, ariaLabel }: RichTextFieldProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  // The markup this component last wrote or read. React must NOT re-render the
  // editable node while someone types in it (the caret would jump), so the DOM
  // is only rewritten when `value` changes to something we didn't produce.
  const lastMarkup = useRef(value);
  // The selection as it stood when focus left for the color picker, which is a
  // real input and takes focus away from the field
  const savedRange = useRef<Range | null>(null);

  const [bubble, setBubble] = useState<{ left: number; top: number; below: boolean } | null>(null);
  const [active, setActive] = useState<ActiveMarks>({ bold: false, italic: false, underline: false });
  const [customColor, setCustomColor] = useState(colors[0] || '#00ffff');

  useLayoutEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    if (value !== lastMarkup.current || editor.innerHTML === '') {
      editor.innerHTML = markupToHtml(value);
      lastMarkup.current = value;
    }
  }, [value]);

  /** Reads the field back out and reports it up. */
  const commit = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const markup = htmlToMarkup(editor);
    lastMarkup.current = markup;
    onChange(markup);
  }, [onChange]);

  /** Places the bubble over the current selection, or hides it when there is none. */
  const syncBubble = useCallback(() => {
    const editor = editorRef.current;
    const wrapper = wrapperRef.current;
    const selection = window.getSelection();
    if (!editor || !wrapper || !selection || selection.rangeCount === 0) {
      setBubble(null);
      return;
    }
    const range = selection.getRangeAt(0);
    if (selection.isCollapsed || !editor.contains(range.commonAncestorContainer)) {
      setBubble(null);
      return;
    }
    const rect = range.getBoundingClientRect();
    const bounds = wrapper.getBoundingClientRect();
    // Above the words by default; below them when the field sits too close to
    // the top of the scrolling modal for the bubble to fit
    const below = rect.top < BUBBLE_CLEARANCE;
    setBubble({
      left: Math.min(Math.max(rect.left + rect.width / 2 - bounds.left, 60), bounds.width - 60),
      top: (below ? rect.bottom : rect.top) - bounds.top,
      below,
    });
    setActive(readActiveMarks());
  }, []);

  useEffect(() => {
    document.addEventListener('selectionchange', syncBubble);
    return () => document.removeEventListener('selectionchange', syncBubble);
  }, [syncBubble]);

  /** Runs a formatting command against the selection, then reads the field back. */
  const run = (command: string, argument?: string) => {
    const editor = editorRef.current;
    if (!editor) return;
    const selection = window.getSelection();
    // Restore the selection first when the color picker took focus off the field
    if (savedRange.current && (!selection || selection.isCollapsed || !editor.contains(selection.anchorNode))) {
      selection?.removeAllRanges();
      selection?.addRange(savedRange.current);
    }
    // Colors as CSS on a span, not the ancient <font> element
    document.execCommand('styleWithCSS', false, 'true');
    document.execCommand(command, false, argument);
    commit();
    setActive(readActiveMarks());
  };

  /** Keeps the caret in the field: a button press would otherwise blur it. */
  const holdSelection = (event: React.MouseEvent) => event.preventDefault();

  const rememberRange = () => {
    const selection = window.getSelection();
    savedRange.current = selection && selection.rangeCount > 0 ? selection.getRangeAt(0).cloneRange() : null;
  };

  return (
    <div className="rtf" ref={wrapperRef}>
      <div
        ref={editorRef}
        className="rtf-input hl-input"
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-label={ariaLabel}
        data-placeholder={placeholder}
        onInput={commit}
        onBlur={() => {
          commit();
          // Leave the bubble up while the pointer is on it; a click elsewhere
          // collapses the selection, which hides it through selectionchange
          rememberRange();
        }}
        onKeyDown={(e) => {
          // One line: Enter would split it into blocks the app cannot render
          if (e.key === 'Enter') e.preventDefault();
        }}
        onPaste={(e) => {
          // Paste as plain text — a copy from a document would otherwise drag
          // fonts, sizes and colors nobody chose into the title
          e.preventDefault();
          const text = e.clipboardData.getData('text/plain').replace(/\s+/g, ' ');
          document.execCommand('insertText', false, text);
        }}
      />

      {bubble && (
        <div
          className={`rtf-bubble${bubble.below ? ' below' : ''}`}
          style={{ left: bubble.left, top: bubble.top }}
          onMouseDown={holdSelection}
        >
          <button
            type="button"
            className={`rtf-tool${active.bold ? ' on' : ''}`}
            title="Bold"
            onMouseDown={holdSelection}
            onClick={() => run('bold')}
          >
            <FiBold size={13} />
          </button>
          <button
            type="button"
            className={`rtf-tool${active.italic ? ' on' : ''}`}
            title="Italic"
            onMouseDown={holdSelection}
            onClick={() => run('italic')}
          >
            <FiItalic size={13} />
          </button>
          <button
            type="button"
            className={`rtf-tool${active.underline ? ' on' : ''}`}
            title="Underline"
            onMouseDown={holdSelection}
            onClick={() => run('underline')}
          >
            <FiUnderline size={13} />
          </button>

          <span className="rtf-divider" />

          {colors.map((color) => (
            <button
              key={color}
              type="button"
              className="rtf-swatch"
              style={{ background: color }}
              title={color}
              onMouseDown={holdSelection}
              onClick={() => run('foreColor', color)}
            />
          ))}
          <label className="rtf-swatch rtf-swatch-custom" title="Custom color">
            <FiDroplet size={11} />
            <input
              type="color"
              value={customColor}
              // The picker steals focus, so hold on to the selection first
              onMouseDown={rememberRange}
              onChange={(e) => {
                setCustomColor(e.target.value);
                run('foreColor', e.target.value);
              }}
            />
          </label>

          <span className="rtf-divider" />

          <button
            type="button"
            className="rtf-tool"
            title="Clear formatting"
            onMouseDown={holdSelection}
            onClick={() => run('removeFormat')}
          >
            <FiX size={13} />
          </button>
        </div>
      )}
    </div>
  );
}
